import axios from 'axios'
import { API_URL } from '@/utils/constants'
import Wallet, { ensureEccLib } from '@/utils/wallet'
import {
	beginWalletFlow,
	classifyWalletError,
	finishWalletFlow,
	itemCountBucket,
	providerSlug,
	sanitizeCollectionSlug,
	errorDetail,
	telemetryAddress,
	telemetryPath
} from '@/utils/wallet/telemetry'
import * as bitcoin from 'bitcoinjs-lib'
import {
	assertSecureWalletSupported,
	compressedPublicKey,
	isBuild3Available,
	PASSTHROUGH_ESCROW_POLICY,
	protocolFromInscription,
	requireBuild3,
	resolveBitcoinOutpoint,
	SecurePurchaseUnavailableError,
	type SecureAssetProtocol,
	type SecurePurchaseCapabilities
} from './secure-purchase'
import { requirePinnedCosigner, toXOnly } from './secure-escrow'
import { assertListingTemplates, sanitizeSignedSaleTemplate } from './secure-passthrough'
import { signedInputSighash, parsePsbt } from './secure-psbt'
import { listingSignPlan, saleTemplateSignPlan, signOwnedPsbts } from './secure-sign'
import { rememberSecureListing } from './secure-ticket-store'

/**
 * Passthrough v4 listing. The seller signs two PSBTs (separate approvals on
 * Xverse mobile, one batched prompt elsewhere): the
 * passthrough (their inscription UTXO into their own escrow) and the sale
 * template (that escrow paying them the price, script path, 0x83). Nothing is
 * broadcast. See docs/PASSTHROUGH_V4.md in the API repository.
 */

export type SecureListingAuthorizePayload = {
	/** Correlation only; never authorizes a listing. */
	attempt_id?: string
	outpoint: string
	protocol: SecureAssetProtocol
	seller_public_key: string
	template_digest: string
	/** Signed passthrough PSBT. */
	psbt: string
	/** Sale template PSBT carrying the seller's script-path pre-signature. */
	sale_psbt: string
}

export type SecureListingItem = {
	inscriptionId?: string
	outpoint?: string
	priceSats: number
	protocol?: SecureAssetProtocol
	inscription?: {
		id?: string
		brc20?: { ticker?: string }
		collection?: { slug?: string }
		rune?: boolean
		alkane?: boolean
	}
}

function sellerPublicKey(): string {
	const raw = Wallet.provider.capabilities.includes('payments-wallet')
		? Wallet.taprootPaymentPublicKey() || Wallet.getPublicKey()
		: Wallet.getPublicKey()
	return compressedPublicKey(raw)
}

function sellerPayoutAddress(): string {
	// Mirrors the legacy escrow builder: payments-style wallets are paid at
	// their balance address, everything else at the taproot address.
	if (Wallet.provider.capabilities.includes('payments-wallet')) {
		return Wallet.balanceAddress() || Wallet.getTaprootAddress()
	}
	return Wallet.getTaprootAddress()
}

export type SecureListingFailure = {
	outpoint: string
	code: string
	message: string
	/** On `protocol_mismatch`: the protocol the server proved the item to be. */
	protocol?: SecureAssetProtocol
}

const SECURE_ASSET_PROTOCOLS: ReadonlySet<string> = new Set([
	'ordinal',
	'rune',
	'alkane',
	'tap',
	'brc20'
])

/**
 * When every item of a protected build was refused only because the client
 * labelled it with the wrong protocol, and the server said which one it
 * proved, the batch can be rebuilt protected under that label. Returns the
 * label to retry with, or null. One protocol for the whole batch, since
 * the bulk route takes one.
 */
export function relabelProtocolFor(
	failures: ReadonlyArray<SecureListingFailure>,
	requested: SecureAssetProtocol
): SecureAssetProtocol | null {
	if (failures.length === 0) return null
	const proven = new Set<string>()
	for (const failure of failures) {
		if (failure.code !== 'protocol_mismatch' || !failure.protocol) return null
		proven.add(failure.protocol)
	}
	if (proven.size !== 1) return null
	const [protocol] = proven
	return protocol !== requested ? (protocol as SecureAssetProtocol) : null
}
export type SecureListingTimings = {
	/** build-bulk round trip (intent creation + templates) */
	buildMs: number
	/** wallet signing, including every approval on Xverse mobile */
	signMs: number
	/** authorize-bulk round trip (verify + publish) */
	authorizeMs: number
	totalMs: number
}
export type SecureListingResult = {
	listed: number
	failures: SecureListingFailure[]
	timings: SecureListingTimings
}

/** Seller-facing text for the per-item codes the bulk endpoints return. */
export function secureListingFailureMessage(code: string): string {
	switch (code) {
		case 'secure_listing_requires_fresh_outpoint':
			return 'This item was listed unprotected before. Send it to yourself, then list it with protection.'
		case 'outpoint_spent_in_mempool':
		case 'asset_not_current':
		case 'asset_index_disagrees':
			return 'This item just moved. Refresh and try again.'
		case 'unconfirmed_asset_outpoint':
			return 'This item has not confirmed yet. Try again after the next block.'
		case 'seller_key_does_not_control_asset':
			return 'This wallet does not own this item.'
		case 'no_supported_assets_on_output':
			return 'Nothing listable on this UTXO.'
		case 'ambiguous_inscription_assets':
			return 'This item shares a UTXO with other inscriptions. Send it to yourself to split them, or list it without protection.'
		case 'protocol_mismatch':
			return 'Snipe protection cannot cover this item type yet. Listing it the standard way.'
		case 'unverified_fungible_inscription':
			return 'This looks like a token transfer we cannot verify. Listing it the standard way.'
		case 'outpoint_not_fully_indexed':
			return 'This item confirmed in the latest block. Protection can list it after the next block; you can list it the standard way now.'
		case 'ord_index_stale':
		case 'asset_index_incomplete':
		case 'asset_index_unavailable':
		case 'asset_index_tip_mismatch':
		case 'asset_index_fork_mismatch':
			return 'Our index is catching up. Try again in a minute.'
		case 'capabilities_fetch_failed':
			return 'Snipe protection is unavailable right now. Try again shortly, or list without it.'
		case 'price_below_dust':
			return 'Price is too low to send on Bitcoin.'
		case 'postage_too_small':
			return 'This item needs at least 330 sats of postage for protection. Send it to yourself with more sats, then list it.'
		case 'already_listed':
			return 'Already listed from another wallet. Delist it there first.'
		case 'wallet_not_connected':
		case 'wallet_connect_rejected':
			return 'Your wallet is not connected to this site. Unlock it, approve the connection, then try again.'
		case 'wallet_extension_conflict':
			return 'Another Bitcoin wallet extension (OKX) is taking over this wallet. Disable it for this site or turn off its "replace other wallets" option, reload, and try again.'
		case 'wallet_probe_timeout':
			return 'Your wallet extension did not respond. Open it, make sure it is unlocked and no other Bitcoin wallet is enabled, reload and try again.'
		case 'provider_missing':
			return 'Your wallet extension is not active in this tab. Enable it, reload the page and connect it again.'
		case 'wallet_account_mismatch':
			return 'Your wallet is on a different account. Switch back to the connected Taproot address, or reconnect.'
		case 'inscription_offset_too_deep':
			return 'This inscription sits too deep in its UTXO for protection.'
		case 'unsupported_seller_script':
			return 'Protection needs the item in a Taproot or native SegWit address.'
		case 'duplicate_outpoint':
			return 'The same item was selected twice.'
		case 'listing_not_found':
			return 'Listing not found. Try again.'
		case 'template_digest_mismatch':
		case 'signed_template_mutated':
		case 'sale_template_mutated':
			return 'The listing changed while signing. Try again.'
		case 'asset_index_tip_changed':
		case 'authorize_tip':
		case 'authorize_tip_changed':
		case 'build_tip':
		case 'build_tip_changed':
			return 'A new block arrived while publishing. Try again.'
		case 'authorization_conflict':
			return 'The listing could not be saved. Refresh and try again; if it repeats, send us the item id.'
		case 'invalid_seller_authorization':
		case 'invalid_seller_sighash':
		case 'passthrough_signature_invalid':
		case 'sale_presignature_invalid':
		case 'sale_signed_on_key_path':
			return 'The wallet signature did not match. Try again.'
		case 'cosigner_unavailable':
		case 'secure_listing_unavailable':
			return 'Snipe protection is unavailable right now.'
		default:
			return 'Listing was rejected. Try again.'
	}
}

type BuiltListing = {
	outpoint: string
	protocol: SecureAssetProtocol
	seller_public_key: string
	template_digest: string
	psbt: string
	sale_psbt: string
	escrow_value: number
	passthrough_txid: string
	escrow_price_sats: number
}

type BulkBuildOutcome = {
	built: BuiltListing[]
	failures: SecureListingFailure[]
}

function rowsOf(data: unknown): Record<string, unknown>[] | null {
	const envelope = data as { items?: unknown; secure_listings?: unknown }
	const rows = Array.isArray(envelope?.items) ? envelope.items : envelope?.secure_listings
	return Array.isArray(rows) ? (rows as Record<string, unknown>[]) : null
}

/**
 * Pair response rows with the request items they belong to. Rows carry their
 * outpoint, so match on that and fall back to position only for rows that
 * lack one; a batch must never be paired by position alone, since the API
 * builds items concurrently.
 */
function rowForItem<T extends { outpoint: string }>(
	rows: Record<string, unknown>[],
	items: T[],
	index: number
): Record<string, unknown> | undefined {
	const wanted = items[index].outpoint.toLowerCase()
	const byOutpoint = rows.find((row) => String(row.outpoint || '').toLowerCase() === wanted)
	if (byOutpoint) return byOutpoint
	const row = rows[index]
	return row && !row.outpoint ? row : undefined
}

function parseBuildRow(
	row: Record<string, unknown>,
	expectedOutpoint: string,
	protocol: SecureAssetProtocol,
	caps: SecurePurchaseCapabilities
): BuiltListing {
	if (
		row.state !== 'authorization_required' ||
		typeof row.psbt !== 'string' ||
		typeof row.sale_psbt !== 'string'
	) {
		throw new SecurePurchaseUnavailableError(
			'invalid_listing_build',
			'Secure listing build did not return both templates'
		)
	}
	const policy = String(row.policy || '')
	if (policy !== PASSTHROUGH_ESCROW_POLICY) {
		throw new SecurePurchaseUnavailableError(
			'unsafe_listing_policy',
			'Listing policy is not supported by this client'
		)
	}
	if (String(row.outpoint).toLowerCase() !== expectedOutpoint.toLowerCase()) {
		throw new SecurePurchaseUnavailableError('stale_listing', 'Listing template outpoint mismatch')
	}
	if (row.protocol && row.protocol !== protocol) {
		throw new SecurePurchaseUnavailableError(
			'protocol_mismatch',
			'Listing template protocol mismatch'
		)
	}
	// The row's co-signer must be the pinned one; the escrow is rebuilt from
	// the pin regardless, so a different key here can only mean a mismatch.
	const pinned = requirePinnedCosigner(caps).toString('hex')
	if (String(row.cosigner_public_key || '').toLowerCase() !== pinned) {
		throw new SecurePurchaseUnavailableError(
			'cosigner_key_unpinned',
			'Listing template names a different co-signer'
		)
	}
	return {
		outpoint: String(row.outpoint),
		protocol,
		seller_public_key: sellerPublicKey(),
		template_digest: String(row.template_digest),
		psbt: row.psbt,
		sale_psbt: row.sale_psbt,
		escrow_value: Number(row.escrow_value),
		escrow_price_sats: Number(row?.escrow_price_sats || 0),
		passthrough_txid: String(row.passthrough_txid || '')
	}
}

/**
 * One round trip: create every private intent and receive both templates per
 * item. Rows are paired to items by outpoint; each is verified locally against
 * the seller's own key, payout script and price before any wallet prompt.
 */
/**
 * Outcomes that mean "the world moved, not the listing": a block arrived
 * mid-build, the asset index is a beat behind, or the API was restarting
 * (5xx, often an HTML error page with no code). One or two retries turn
 * these into a listing instead of a "please try again" toast.
 */
const TRANSIENT_LISTING_CODES = new Set([
	'build_tip_changed',
	'build_tip',
	'authorize_tip',
	'authorize_tip_changed',
	'asset_index_tip_changed',
	'asset_index_tip_mismatch',
	'asset_index_incomplete',
	'asset_index_unavailable',
	'chain_tip_unavailable',
	'ord_index_stale',
	'secure_listing_unavailable'
])
const TRANSIENT_RETRY_DELAYS_MS = [800, 2000]

export function isTransientListingCode(code: string): boolean {
	return TRANSIENT_LISTING_CODES.has(code) || /^http_5\d\d$/.test(code)
}

async function withTransientRetry<T>(request: () => Promise<T>): Promise<T> {
	let attempt = 0
	for (;;) {
		try {
			return await request()
		} catch (error) {
			const delay = TRANSIENT_RETRY_DELAYS_MS[attempt]
			if (
				delay === undefined ||
				!(error instanceof SecurePurchaseUnavailableError) ||
				!isTransientListingCode(error.code)
			) {
				throw error
			}
			attempt += 1
			await new Promise((resolve) => setTimeout(resolve, delay))
		}
	}
}

async function buildSecureListingsBulk(
	items: Array<{ outpoint: string; priceSats: number }>,
	protocol: SecureAssetProtocol,
	caps: SecurePurchaseCapabilities,
	attemptId?: string
): Promise<BulkBuildOutcome> {
	await ensureEccLib()
	requirePinnedCosigner(caps)
	const { data } = await withTransientRetry(async () => {
		const response = await axios.post(
			`${API_URL}/market/secure-listing/build-bulk`,
			{
				attempt_id: attemptId,
				protocol,
				seller_public_key: sellerPublicKey(),
				seller_address: sellerPayoutAddress(),
				items: items.map((item) => ({
					outpoint: item.outpoint,
					escrow_price_sats: item.priceSats
				}))
			},
			{ validateStatus: () => true, headers: { 'Cache-Control': 'no-store' } }
		)
		if (response.status >= 400) {
			throw new SecurePurchaseUnavailableError(
				response.data?.code || `http_${response.status}`,
				response.data?.message || 'Secure listing build failed'
			)
		}
		return response
	})
	const rows = rowsOf(data)
	if (!rows || rows.length !== items.length) {
		throw new SecurePurchaseUnavailableError(
			'invalid_listing_build',
			'Secure listing build returned an unexpected row count'
		)
	}
	const sellerXOnly = toXOnly(sellerPublicKey())
	const sellerScript = bitcoin.address.toOutputScript(
		sellerPayoutAddress(),
		bitcoin.networks.bitcoin
	)
	const built: BuiltListing[] = []
	const failures: SecureListingFailure[] = []
	items.forEach((item, index) => {
		const outpoint = item.outpoint
		const row = rowForItem(rows, items, index)
		if (!row) {
			failures.push({
				outpoint,
				code: 'invalid_listing_build',
				message: secureListingFailureMessage('invalid_listing_build')
			})
			return
		}
		if (row.error) {
			const code = String(row.code || 'secure_listing_rejected')
			const proven = typeof row.protocol === 'string' ? row.protocol : ''
			failures.push({
				outpoint,
				code,
				message: secureListingFailureMessage(code),
				...(code === 'protocol_mismatch' && SECURE_ASSET_PROTOCOLS.has(proven)
					? { protocol: proven as SecureAssetProtocol }
					: {})
			})
			return
		}
		// Per item: one unusable template must not stop the rest of the batch.
		try {
			const parsed = parseBuildRow(row, outpoint, protocol, caps)
			const checked = assertListingTemplates({
				passthroughPsbtHex: parsed.psbt,
				salePsbtHex: parsed.sale_psbt,
				expectedOutpoint: outpoint,
				sellerXOnly,
				expectedSellerScript: sellerScript,
				expectedSellerSats: item.priceSats
			})
			if (parsed.passthrough_txid && parsed.passthrough_txid !== checked.passthroughTxid) {
				throw new SecurePurchaseUnavailableError(
					'stale_listing',
					'Passthrough txid does not match its template'
				)
			}
			built.push({
				...parsed,
				passthrough_txid: checked.passthroughTxid,
				escrow_value: checked.escrowValue
			})
		} catch (error) {
			const code =
				error instanceof SecurePurchaseUnavailableError ? error.code : 'invalid_listing_build'
			const message =
				error instanceof SecurePurchaseUnavailableError
					? error.message
					: secureListingFailureMessage(code)
			failures.push({ outpoint, code, message })
		}
	})
	return { built, failures }
}

async function authorizeSecureListingsBulk(
	payloads: SecureListingAuthorizePayload[]
): Promise<{ listed: number; failures: SecureListingFailure[] }> {
	await ensureEccLib()
	const { data } = await withTransientRetry(async () => {
		const response = await axios.post(
			`${API_URL}/market/secure-listing/authorize-bulk`,
			{ items: payloads },
			{ validateStatus: () => true, headers: { 'Cache-Control': 'no-store' } }
		)
		if (response.status >= 400) {
			throw new SecurePurchaseUnavailableError(
				response.data?.code || `http_${response.status}`,
				response.data?.message || 'Secure listing authorization failed'
			)
		}
		return response
	})
	// A single-item authorize returns the item itself.
	const rows =
		payloads.length === 1 &&
		data &&
		!Array.isArray(data?.items) &&
		!Array.isArray(data?.secure_listings)
			? [data as Record<string, unknown>]
			: rowsOf(data)
	if (!rows || rows.length !== payloads.length) {
		throw new SecurePurchaseUnavailableError(
			'invalid_listing_authorize',
			'Secure listing authorize returned an unexpected row count'
		)
	}
	let listed = 0
	const failures: SecureListingFailure[] = []
	payloads.forEach((payload, index) => {
		const outpoint = payload.outpoint
		const row = rowForItem(rows, payloads, index)
		if (!row) {
			failures.push({
				outpoint,
				code: 'secure_listing_rejected',
				message: secureListingFailureMessage('secure_listing_rejected')
			})
			return
		}
		if (row.error) {
			const code = String(row.code || 'secure_listing_rejected')
			failures.push({ outpoint, code, message: secureListingFailureMessage(code) })
			return
		}
		if (row.state && row.state !== 'listed') {
			failures.push({
				outpoint,
				code: 'listing_not_public',
				message: 'Secure listing did not become publicly listed'
			})
			return
		}
		rememberSecureListing(outpoint)
		listed += 1
	})
	return { listed, failures }
}

/**
 * Sign every passthrough and sale template through the wallet adapter, then check
 * each signature is the kind the API will accept: a plain SIGHASH_ALL or
 * DEFAULT on the passthrough, a 0x83 script-path signature on the sale.
 */
async function signListingTemplates(
	built: BuiltListing[]
): Promise<SecureListingAuthorizePayload[]> {
	const plans = built.flatMap((row) => [
		listingSignPlan(row.psbt),
		saleTemplateSignPlan(row.sale_psbt)
	])
	const signed = await signOwnedPsbts(plans)
	if (signed.length !== plans.length) {
		throw new SecurePurchaseUnavailableError(
			'sign_failed',
			'Wallet did not sign every listing template'
		)
	}
	const sellerXOnly = toXOnly(sellerPublicKey())
	return built.map((row, index) => {
		const passthrough = signed[index * 2]
		const sale = signed[index * 2 + 1]
		for (const data of parsePsbt(passthrough).data.inputs) {
			const sighash = signedInputSighash(data)
			if (sighash === undefined) {
				throw new SecurePurchaseUnavailableError(
					'sign_failed',
					'Wallet returned the passthrough without a signature'
				)
			}
			if (
				sighash !== bitcoin.Transaction.SIGHASH_DEFAULT &&
				sighash !== bitcoin.Transaction.SIGHASH_ALL
			) {
				throw new SecurePurchaseUnavailableError(
					'listing_sighash',
					'Passthrough must be signed with SIGHASH_ALL'
				)
			}
		}
		const salePsbt = sanitizeSignedSaleTemplate(sale, sellerXOnly)
		return {
			outpoint: row.outpoint,
			protocol: row.protocol,
			escrow_price_sats: row.escrow_price_sats,
			seller_public_key: row.seller_public_key,
			template_digest: row.template_digest,
			psbt: passthrough,
			sale_psbt: salePsbt
		}
	})
}

async function trackListingPhase<T>(
	attemptId: string,
	protocol: SecureAssetProtocol,
	event: 'listing_build' | 'listing_sign' | 'listing_authorize',
	run: () => Promise<T>,
	collectionSlug?: string,
	isProtected = true
): Promise<T> {
	const stage =
		event === 'listing_build'
			? 'build_psbt'
			: event === 'listing_sign'
			? 'sign_psbts'
			: 'authorize_listing'
	const details = {
		attempt_id: attemptId,
		protected: isProtected,
		asset_protocol: telemetryProtocol(protocol),
		item_count_bucket: itemCountBucket(1),
		collection_slug: sanitizeCollectionSlug(collectionSlug)
	}
	const flow = beginWalletFlow(
		event,
		providerSlug(Wallet.display_name() || Wallet.type()),
		'listing_flow',
		stage,
		details
	)
	try {
		const value = await run()
		finishWalletFlow(flow, stage, 'success', details)
		return value
	} catch (error) {
		const category = classifyWalletError(error)
		const sellerAddress = (() => {
			try {
				return telemetryAddress(Wallet.getTaprootAddress())
			} catch {
				return undefined
			}
		})()
		finishWalletFlow(flow, stage, category === 'user_rejected' ? 'cancelled' : 'failure', {
			...details,
			error_category: category,
			provider_error_code:
				error && typeof error === 'object' && typeof (error as any).code === 'string'
					? String((error as any).code).slice(0, 32)
					: undefined,
			// Failure context: whose wallet, what the error said, which page.
			...(category !== 'user_rejected' && sellerAddress ? { seller_address: sellerAddress } : {}),
			...(errorDetail(error) ? { error_detail: errorDetail(error) } : {}),
			...(telemetryPath() ? { path: telemetryPath() } : {})
		})
		throw error
	}
}
export async function authorizeSecureListing(
	payload: SecureListingAuthorizePayload
): Promise<void> {
	const attemptId = payload.attempt_id || crypto.randomUUID()
	return trackListingPhase(attemptId, payload.protocol, 'listing_authorize', async () => {
		const { listed, failures } = await authorizeSecureListingsBulk([
			{ ...payload, attempt_id: attemptId }
		])
		if (listed !== 1) {
			const failure = failures[0]
			throw new SecurePurchaseUnavailableError(
				failure?.code || 'listing_not_public',
				failure?.message || 'Secure listing did not become publicly listed'
			)
		}
	})
}
export async function prepareSecureListing(
	item: SecureListingItem
): Promise<SecureListingAuthorizePayload> {
	const attemptId = crypto.randomUUID()
	const protocol = item.protocol || protocolFromInscription(item.inscription || {})
	const built = await trackListingPhase(
		attemptId,
		protocol,
		'listing_build',
		async () => {
			await ensureEccLib()
			assertSecureWalletSupported(Wallet.type())
			const outpoint = await resolveBitcoinOutpoint(
				item.outpoint || item.inscriptionId || item.inscription?.id || ''
			)
			const caps = await requireBuild3(protocol)
			const { built, failures } = await buildSecureListingsBulk(
				[{ outpoint, priceSats: item.priceSats }],
				protocol,
				caps,
				attemptId
			)
			if (failures.length > 0 || built.length !== 1) {
				const failure = failures[0]
				throw new SecurePurchaseUnavailableError(
					failure?.code || 'invalid_listing_build',
					failure?.message || 'Secure listing build failed'
				)
			}
			return built
		},
		item.inscription?.collection?.slug
	)
	const [payload] = await trackListingPhase(
		attemptId,
		protocol,
		'listing_sign',
		() => signListingTemplates(built),
		item.inscription?.collection?.slug
	)
	return { ...payload, attempt_id: attemptId }
}

/**
 * Telemetry for the standard (unprotected) listing a protected attempt falls
 * back to. Reuses the protected attempt's id so the server rejection, the
 * client failure and the fallback's outcome read as one thread.
 */
export function trackStandardListingPhase<T>(
	attemptId: string,
	protocol: SecureAssetProtocol,
	event: 'listing_build' | 'listing_sign' | 'listing_authorize',
	run: () => Promise<T>,
	collectionSlug?: string
): Promise<T> {
	return trackListingPhase(attemptId, protocol, event, run, collectionSlug, false)
}

export async function listSecureItems(
	items: SecureListingItem[],
	options: { attemptId?: string; relabelled?: boolean } = {}
): Promise<SecureListingResult> {
	const attemptId = options.attemptId || crypto.randomUUID()
	await ensureEccLib()
	if (items.length === 0) {
		throw new SecurePurchaseUnavailableError('invalid_request', 'Select at least one item to list')
	}
	assertSecureWalletSupported(Wallet.type())
	const protocol = items[0].protocol || protocolFromInscription(items[0].inscription || {})
	for (const item of items) {
		const itemProtocol = item.protocol || protocolFromInscription(item.inscription || {})
		if (itemProtocol !== protocol) {
			throw new SecurePurchaseUnavailableError(
				'mixed_protocol',
				'Secure listing cannot mix asset protocols in one batch'
			)
		}
	}
	// Telemetry: one flow per phase (build, sign, authorize) so a busted
	// listing shows up as a failure at the phase that broke, with the wallet
	// and collection attached and never any transaction bytes.
	const provider = providerSlug(Wallet.display_name() || Wallet.type())
	const telemetry = {
		attempt_id: attemptId,
		protected: true as const,
		collection_slug: sanitizeCollectionSlug(items[0].inscription?.collection?.slug),
		asset_protocol: telemetryProtocol(protocol),
		item_count_bucket: itemCountBucket(items.length)
	}
	const failed = (error: unknown, stage: 'build_psbt' | 'sign_psbts' | 'authorize_listing') => {
		const category = classifyWalletError(error)
		const detail = errorDetail(error)
		const seller = (() => {
			try {
				return telemetryAddress(Wallet.getTaprootAddress())
			} catch {
				return undefined
			}
		})()
		return {
			...telemetry,
			error_category: category,
			provider_error_code:
				error && typeof error === 'object' && typeof (error as any).code === 'string'
					? String((error as any).code).slice(0, 32)
					: undefined,
			stage,
			// Same failure context the standard path sends: what the wallet or
			// our guard said (e.g. injected= for a probe timeout), whose wallet,
			// which page.
			...(detail ? { error_detail: detail } : {}),
			...(category !== 'user_rejected' && seller ? { seller_address: seller } : {}),
			...(telemetryPath() ? { path: telemetryPath() } : {})
		}
	}

	const startedAt = performance.now()
	const buildFlow = beginWalletFlow(
		'listing_build',
		provider,
		'listing_flow',
		'build_psbt',
		telemetry
	)
	let outpoints: string[]
	let caps: Awaited<ReturnType<typeof requireBuild3>>
	let built: BuiltListing[]
	let failures: SecureListingFailure[]
	const buildStartedAt = performance.now()
	try {
		;[outpoints, caps] = await Promise.all([
			Promise.all(
				items.map((item) =>
					resolveBitcoinOutpoint(item.outpoint || item.inscriptionId || item.inscription?.id || '')
				)
			),
			requireBuild3(protocol)
		])
		;({ built, failures } = await buildSecureListingsBulk(
			outpoints.map((outpoint, index) => ({ outpoint, priceSats: items[index].priceSats })),
			protocol,
			caps,
			attemptId
		))
	} catch (error) {
		finishWalletFlow(buildFlow, 'build_psbt', 'failure', failed(error, 'build_psbt'))
		throw error
	}
	const buildMs = performance.now() - buildStartedAt
	if (built.length === 0) {
		finishWalletFlow(buildFlow, 'build_psbt', 'failure', {
			...telemetry,
			error_category: 'invalid_response',
			provider_error_code: failures[0]?.code ? String(failures[0].code).slice(0, 32) : undefined
		})
		// The server proved a different protocol for every item (a DMT
		// element under a plain collection slug is TAP): rebuild protected
		// under that label, once, on the same attempt, rather than dropping
		// to an unprotected listing.
		// Only when protection covers the proven protocol: retrying under a
		// protocol the rollout has off threw `build3_unavailable` from inside
		// the retry (an unhinted error=unknown line) instead of the standard
		// fallback the caller already runs for protocol_mismatch.
		const relabel = relabelProtocolFor(failures, protocol)
		if (relabel && !options.relabelled && isBuild3Available(caps, relabel)) {
			return listSecureItems(
				items.map((item) => ({ ...item, protocol: relabel })),
				{ attemptId, relabelled: true }
			)
		}
		return {
			listed: 0,
			failures,
			timings: { buildMs, signMs: 0, authorizeMs: 0, totalMs: performance.now() - startedAt }
		}
	}
	finishWalletFlow(buildFlow, 'build_psbt', 'success', telemetry)

	const signFlow = beginWalletFlow(
		'listing_sign',
		provider,
		'listing_flow',
		'sign_psbts',
		telemetry
	)
	const signStartedAt = performance.now()
	let payloads: SecureListingAuthorizePayload[]
	try {
		payloads = (await signListingTemplates(built)).map((payload) => ({
			...payload,
			attempt_id: attemptId
		}))
	} catch (error) {
		finishWalletFlow(
			signFlow,
			'sign_psbts',
			classifyWalletError(error) === 'user_rejected' ? 'cancelled' : 'failure',
			failed(error, 'sign_psbts')
		)
		throw error
	}
	finishWalletFlow(signFlow, 'sign_psbts', 'success', telemetry)
	const signMs = performance.now() - signStartedAt

	const authorizeFlow = beginWalletFlow(
		'listing_authorize',
		provider,
		'listing_flow',
		'authorize_listing',
		telemetry
	)
	const authorizeStartedAt = performance.now()
	let authorized: Awaited<ReturnType<typeof authorizeSecureListingsBulk>>
	try {
		authorized = await authorizeSecureListingsBulk(payloads)
	} catch (error) {
		finishWalletFlow(
			authorizeFlow,
			'authorize_listing',
			'failure',
			failed(error, 'authorize_listing')
		)
		throw error
	}
	const authorizeMs = performance.now() - authorizeStartedAt
	finishWalletFlow(
		authorizeFlow,
		'authorize_listing',
		authorized.listed > 0 ? 'success' : 'failure',
		authorized.listed > 0
			? telemetry
			: {
					...telemetry,
					error_category: 'invalid_response',
					provider_error_code: authorized.failures[0]?.code
						? String(authorized.failures[0].code).slice(0, 32)
						: undefined
			  }
	)
	return {
		listed: authorized.listed,
		failures: [...failures, ...authorized.failures],
		timings: { buildMs, signMs, authorizeMs, totalMs: performance.now() - startedAt }
	}
}

/**
 * Toast copy for a bulk secure listing: what listed, what did not and why.
 */
/**
 * Rejections about the *shape* of the output, not its ownership or state:
 * protection moves exactly one inscription on enough postage; the standard
 * listing does not care. These items can still be listed the old way.
 */
export const LEGACY_LISTABLE_CODES = new Set([
	'ambiguous_inscription_assets',
	'postage_too_small',
	'unverified_fungible_inscription',
	'inscription_offset_too_deep',
	'unsupported_seller_script',
	// The server reads the inscription's protocol itself and refuses a
	// template requested under another label (DMT elements in "dmt-*"
	// collections come back as TAP). Protection cannot take the item as
	// labelled; the standard listing can, as it always has.
	'protocol_mismatch'
])

/**
 * Rejections the seller fixes by moving the item onto its own, well-postaged
 * UTXO: a self-send built with postage splits the shared UTXO by sat offset
 * (the API's send-bulk), which the UTXO manager's BTC splitter does not do.
 */
export const UTXO_SPLIT_CODES = new Set([
	'ambiguous_inscription_assets',
	'postage_too_small',
	'secure_listing_requires_fresh_outpoint'
])

export function needsUtxoSplit(failures: ReadonlyArray<{ code: string }>): boolean {
	return failures.some((failure) => UTXO_SPLIT_CODES.has(failure.code))
}

export type IsolateTarget = { slug: string | null; inscriptionId: string }

/**
 * The item a "Send to yourself" action should isolate: the first UTXO-shape
 * failure, mapped back to the row that was listed. Rows match by the UTXO
 * outpoint the API reports; a lone row matches regardless, since the
 * failure can only be about it. Null when nothing can be pointed at.
 */
export function isolateTargetForFailures(
	failures: ReadonlyArray<{ code: string; outpoint: string }>,
	rows: ReadonlyArray<{ inscriptionId: string; outpoint?: string | null }>,
	slug?: string | null
): IsolateTarget | null {
	const failure = failures.find((f) => UTXO_SPLIT_CODES.has(f.code))
	if (!failure) return null
	const wanted = failure.outpoint.toLowerCase()
	const row =
		rows.find((r) => (r.outpoint || '').toLowerCase() === wanted) ??
		rows.find((r) => r.inscriptionId.toLowerCase() === wanted) ??
		(rows.length === 1 ? rows[0] : undefined)
	if (!row?.inscriptionId) return null
	return { slug: slug || null, inscriptionId: row.inscriptionId }
}

export function describeSecureListingResult(
	result: SecureListingResult,
	noun: string
): { title: string; description: string; failed: boolean } {
	const { listed, failures } = result
	const lines: string[] = []
	if (listed > 0) {
		// Same copy as a normal listing: the seller chose protection already.
		lines.push(`You listed ${listed} ${listed === 1 ? noun.replace(/s$/, '') : noun}`)
	}
	for (const failure of failures) {
		lines.push(`${failure.outpoint.slice(0, 8)}…: ${failure.message}`)
	}
	return {
		title: listed > 0 ? (failures.length > 0 ? 'Partially listed' : 'Success!') : 'Listing failed',
		description: lines.join('\n'),
		failed: listed === 0
	}
}

function telemetryProtocol(
	protocol: string
): 'tap' | 'rune' | 'brc20' | 'alkane' | 'ordinal' | 'unknown' {
	switch (protocol) {
		case 'tap':
		case 'rune':
		case 'brc20':
		case 'alkane':
		case 'ordinal':
			return protocol
		default:
			return 'unknown'
	}
}
